import { createRouter, createWebHistory } from "vue-router";
import Start from "@/views/Start.vue";
import Login from "@/views/Login.vue";
import SelfRegistration from "@/views/SelfRegistration.vue";
import PeerMentorHeader from "@/components/PeerMentorHeader.vue";
import Logout from "@/views/Logout.vue";
import LogoutHeader from "@/components/LogoutHeader.vue";

// NB only the router is aware of URLs. The route names are used exclusively throughout
// the rest of the application
// Meta field defaults:
// requiresAuth: true
// forceActions: true
// allowedUserTypes: no default. Not required if requiresAuth is false, otherwise must be an array
//  The array's elements should be user type values that match the values returned by app.getUserTypes
//
// Meta filed defaults will be set automatically. You only need to specify meta values when
// overriding the default values. Note that setting requiresAuth to false automatically sets
// forceActions to false.
const routes = [
  {
    path: "/self-registration", // Redirect to login until we have a public home page built
    name: "SelfRegistration",
    components: {
      default: SelfRegistration,
      Header: PeerMentorHeader,
    },
    props: {
      Header: {
        showMenu: false,
        showLoginText: false,
        showTagLine: false,
        showAppName: false,
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/",
    redirect: "/login", // Redirect to login until we have a public home page built
    name: "Start",
    components: {
      default: Start,
      Header: PeerMentorHeader,
    },
    props: {
      Header: {
        showMenu: false,
        showLoginText: false,
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/login",
    name: "Login",
    components: {
      default: Login,
      Header: PeerMentorHeader,
    },
    props: {
      Header: {
        showMenu: false,
        showLoginText: true,
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/first-login",
    name: "FirstLogin",
    components: {
      default: () => import("../views/FirstLogin.vue"),
      Header: PeerMentorHeader,
    },
    props: {
      Header: { showMenu: false },
    },
    meta: {
      forceActions: false, // tells router.beforeEach() to serve the view even if user requires action(s)
      showUrgentAlert: false,
      requiresAuth: false,
    },
  },
  {
    path: "/email-reset",
    name: "ResetEmail",
    components: {
      default: () => import("../views/EmailReset.vue"),
      Header: PeerMentorHeader,
    },
    props: {
      Header: { showMenu: false },
    },
    meta: {
      forceActions: false, // tells router.beforeEach() to serve the view even if user requires action(s)
      showUrgentAlert: false,
      requiresAuth: false,
    },
  },
  {
    path: "/resend-invite",
    name: "ResendInvitation",
    components: {
      default: () => import("../views/ResendInvitation.vue"),
      Header: PeerMentorHeader,
    },
    props: {
      default: {
        pageTitle: "screens.Apply.pageTitle",
      },
      Header: { showMenu: false },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/privacy",
    name: "Privacy",
    components: {
      default: () => import("../views/Privacy.vue"),
      Header: () => import("../components/LegalHeader.vue"),
    },
    props: {
      Header: { showMenu: false },
    },
    meta: {
      forceActions: false, // tells router.beforeEach() to serve the view even if user requires action(s)
      showUrgentAlert: false,
      requiresAuth: false,
    },
  },
  {
    path: "/terms",
    name: "Terms",
    components: {
      default: () => import("../views/Terms.vue"),
      Header: () => import("../components/LegalHeader.vue"),
    },
    props: {
      Header: { showMenu: false },
    },
    meta: {
      forceActions: false, // tells router.beforeEach() to serve the view even if user requires action(s)
      showUrgentAlert: false,
      requiresAuth: false,
    },
  },
  {
    path: "/apply/:type",
    name: "ApplicationStart",
    components: {
      default: () => import("../views/ApplicationStart.vue"),
      Header: () => import("../components/ApplicationHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Apply.pageTitle",
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/email-verification",
    name: "ApplicationEmailSubmitted",
    components: {
      default: () => import("../views/ApplicationEmailVerification.vue"),
      Header: () => import("../components/EmailSubmittedHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Apply.pageTitle",
      },
    },
    meta: { requiresAuth: false },
  },
  // {
  //   path: "/mentor-apply",
  //   name: "ApplicationStart",
  //   components: {
  //     default: () =>
  //       import(
  //         "../views/ApplicationStart.vue"
  //       ),
  //       Header: () =>
  //         import(
  //           "../components/ApplicationPeerHeader.vue"
  //       ),
  //   },
  //   props: {
  //     default: {
  //       pageTitle: "screens.Apply.mentorHeader",
  //     },
  //     Header: {
  //       type: "mentor",
  //     }
  //   },
  //   meta: { requiresAuth: false },
  // },
  {
    path: "/peer-application",
    name: "PeerApplication",
    components: {
      default: () => import("../views/ApplicationPeer.vue"),
      Header: () => import("../components/ApplicationPeerHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Apply.menteeHeader",
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/mentor-application",
    name: "MentorApplication",
    components: {
      default: () => import("../views/ApplicationMentor.vue"),
      Header: () => import("../components/ApplicationMentorHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Apply.mentorHeader",
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/application",
    name: "Application",
    components: {
      default: () => import("../views/Application.vue"),
      Header: () => import("../components/ApplicationPeerHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Apply.pageHeader",
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    components: {
      default: () => import("../views/ForgotPassword.vue"),
      Header: () => import("../components/ForgotPasswordHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ForgotPassword.pageTitle",
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/forgot-password-confirmation",
    name: "ForgotPasswordConfirmation",
    components: {
      default: () => import("../views/ForgotPasswordConfirmation.vue"),
      Header: () =>
        import("../components/ForgotPasswordConfirmationHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ForgotPasswordConfirmation.pageTitle",
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/forgot-password-reset",
    name: "ForgotPasswordReset",
    components: {
      default: () => import("../views/ForgotPasswordReset.vue"),
      Header: PeerMentorHeader,
    },
    props: {
      default: {
        pageTitle: "screens.ForgotPasswordConfirmation.pageTitle",
      },
      Header: {
        showMenu: false,
        showLoginText: false,
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/logout/:survey",
    name: "Logout",
    components: {
      default: Logout,
      Header: LogoutHeader,
    },
    props: {
      default: {
        pageTitle: "screens.Logout.pageTitle",
      },
    },
    meta: { requiresAuth: false },
  },
  {
    path: "/consent",
    name: "Consent",
    // route level code-splitting
    // this generates a separate chunk (about.[hash].js) for this route
    // which is lazy-loaded when the route is visited.
    components: {
      default: () => import("../views/Interstitial.vue"),
      Header: PeerMentorHeader,
    },
    props: {
      Header: { showMenu: false },
    },
    meta: {
      forceActions: false, // tells router.beforeEach() to serve the view even if user requires action(s)
      showUrgentAlert: false,
      allowedUserTypes: ["mentee", "mentor", "coord"],
    },
  },
  {
    path: "/survey",
    name: "Survey",
    components: {
      default: () => import("../views/Survey.vue"),
      Header: PeerMentorHeader,
    },
    meta: {
      forceActions: false, // tells router.beforeEach() to serve the view even if user requires action(s)
      showUrgentAlert: false,
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/welcome",
    name: "PeerMentorSplashScreen",
    components: {
      default: () => import("../views/PeerMentorSplashScreen.vue"),
      Header: PeerMentorHeader,
    },
    meta: {
      forceActions: true, // tells router.beforeEach() to serve the view even if user requires action(s)
      showUrgentAlert: false,
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/home",
    name: "Home",
    components: {
      default: () => import("../views/PeerMentorHome.vue"),
      Header: PeerMentorHeader,
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorHome.pageTitle",
      },
      Header: {
        headerClass: "dashboard--header",
        titleClass: "dashboard--title",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/videos/:id",
    name: "PeerMentorVideos",
    components: {
      default: () => import("../views/PeerMentorVideos.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorVideos.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfo.backLinkText",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentor,mentee"],
    },
  },
  {
    path: "/chat/:user",
    name: "Messaging",
    components: {
      default: () => import("../views/PeerMentorMessaging.vue"),
      Header: () => import("../components/PeerMentorMessagingHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorMessaging.pageTitle",
      },
      Header: {
        showBorder: false,
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/questions-for-mentor",
    name: "PeerQuestions",
    components: {
      default: () => import("../views/PeerQuestions.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorHome.pageTitle",
      },
      Header: {
        backLinkText: "global.defaultBackLinkText",
        backLinkRoute: "SkillsList",
      },
    },
    meta: {
      allowedUserTypes: ["mentee"],
    },
  },
  {
    path: "/questions-for-doctor",
    name: "PeerQuestionsDoctor",
    components: {
      default: () => import("../views/PeerQuestionsDoctor.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorHome.pageTitle",
      },
      Header: {
        backLinkText: "global.defaultBackLinkText",
        backLinkRoute: "SkillsList",
      },
    },
    meta: {
      allowedUserTypes: ["mentee"],
    },
  },
  // This version of the chat URL sets the session notes to the default view
  {
    path: "/chat/:user",
    name: "Messaging/SessionNotes",
    components: {
      default: () => import("../views/PeerMentorMessaging.vue"),
      Header: () => import("../components/PeerMentorHeaderShort.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorMessaging.pageTitle",
        activeTab: "Session Notes",
      },
      Header: {
        showBorder: false,
      },
    },
    meta: {
      allowedUserTypes: ["mentor"],
    },
  },
  {
    path: "/session-note/:user/:note",
    name: "MentorSessionNotesDetail",
    components: {
      default: () => import("../views/MentorSessionNotesDetail.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.MentorSessionNotesDetail.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfo.backLinkText",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentor"],
    },
  },
  {
    path: "/add-session-notes",
    name: "SessionNotesAdd",
    components: {
      default: () => import("../views/SessionNotesAdd.vue"),
      Header: () => import("../components/PeerMentorHeaderShort.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.SessionNotesAdd.pageTitle",
      },
    },
    meta: {
      allowedUserTypes: ["mentor"],
    },
  },
  {
    path: "/add-session-notes/:user",
    name: "SessionNotesAddUser",
    components: {
      default: () => import("../views/SessionNotesAdd.vue"),
      Header: () => import("../components/PeerMentorHeaderShort.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.SessionNotesAdd.pageTitle",
      },
    },
    meta: {
      allowedUserTypes: ["mentor"],
    },
  },
  {
    path: "/resource-library",
    name: "ResourcesList",
    components: {
      default: () => import("../views/ResourcesList.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitle",
        defaultTab: "All Articles",
        category: "health", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/resource-library/:id",
    name: "ResourceDetail",
    components: {
      default: () => import("../views/ResourceDetail.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitle",
      },
      Header: {
        backLinkRoute: "ResourcesList",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/transplant-education/:id",
    name: "TransplantEducationDetail",
    components: {
      default: () => import("../views/ResourceDetail.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitle",
      },
      Header: {
        backLinkRoute: "TransplantEducation",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/livng-donor-education/:id",
    name: "LivingDonorEducationDetail",
    components: {
      default: () => import("../views/ResourceDetail.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitle",
      },
      Header: {
        backLinkRoute: "LivingDonorEducation",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/admin/health-library",
    name: "AdminHealthLibrary",
    components: {
      default: () => import("../views/AdminResourcesListNested.vue"),
      Header: () => import("../components/AdminHeaderResources.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitle",
        defaultTab: "All Articles",
        category: "health", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/health-library/:id",
    name: "AdminHealthLibraryDetail",
    components: {
      default: () => import("../views/AdminResourcesListNestedDetail.vue"),
      Header: () => import("../components/AdminHeaderResourcesNested.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitle",
        defaultTab: "All Articles",
        category: "health", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/tips",
    name: "MentoringTips",
    components: {
      default: () => import("../views/PeerMentorTips.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Tips.pageTitleMentor",
      },
    },
    meta: {
      allowedUserTypes: ["mentor"],
    },
  },
  {
    path: "/skills",
    name: "SkillsList",
    components: {
      default: () => import("../views/SkillsList.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Tips.pageTitleMentor",
      },
      Header: {
        backLinkText: "global.buttons.returnHome",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/skills/:id",
    name: "SkillsDetail",
    components: {
      default: () => import("../views/SkillsDetail.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Tips.pageTitleMentor",
      },
      Header: {
        backLinkText: "global.defaultBackLinkText",
        backLinkRoute: "SkillsList",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/admin/skills/:id",
    name: "AdminSkillsDetail",
    components: {
      default: () => import("../views/AdminSkillsDetail.vue"),
      Header: () => import("../components/AdminHeaderResources.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Tips.pageTitleMentor",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/questions",
    name: "DoctorQuestions",
    components: {
      default: () => import("../views/PeerMentorTips.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.Tips.pageTitlePeer",
      },
    },
    meta: {
      allowedUserTypes: ["mentee"],
    },
  },
  {
    path: "/mange-health-resources",
    name: "ManageHealthResources",
    components: {
      default: () => import("../views/ResourcesList.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "manage_health", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/admin/patient-stories",
    name: "AdminPatientStories",
    components: {
      default: () => import("../views/AdminResourcesList.vue"),
      Header: () => import("../components/AdminHeaderResources.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "manage_health", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/community-resources",
    name: "CommunityResources",
    components: {
      default: () => import("../views/ResourcesList.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "community", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/admin/community-resources",
    name: "AdminCommunityResources",
    components: {
      default: () => import("../views/AdminResourcesList.vue"),
      Header: () => import("../components/AdminHeaderResources.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "community", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/resources",
    name: "ClientResources",
    components: {
      default: () => import("../views/ResourcesList.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "resources", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/transplant-education",
    name: "TransplantEducation",
    components: {
      default: () => import("../views/ResourcesList.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "transplant_education", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/living-donor-education",
    name: "LivingDonorEducation",
    components: {
      default: () => import("../views/ResourcesList.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "living_donor_education", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/admin/client-resources",
    name: "AdminClientResources",
    components: {
      default: () => import("../views/AdminResourcesList.vue"),
      Header: () => import("../components/AdminHeaderResources.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.ResourcesList.pageTitleCommunity",
        defaultTab: "All Articles",
        category: "resources", // We'll replace this with the client-specific value in the component
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/account-info",
    name: "AccountInfo",
    components: {
      default: () => import("../views/PeerMentorAccountInfo.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfo.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfo.backLinkText",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/account-info/personal",
    name: "AccountInfoPersonal",
    components: {
      default: () => import("../views/PeerMentorAccountInfoPersonal.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoPersonal.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfoPersonal.backLinkText",
        backLinkRoute: "AccountInfo",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/account-info/edit_email",
    name: "AccountInfoEmail",
    components: {
      default: () => import("../views/EditEmail.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoPassword.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfoEmail.backLinkText",
        backLinkRoute: "AccountInfo",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/account-info/password",
    name: "AccountInfoPassword",
    components: {
      default: () => import("../views/PeerMentorAccountInfoPassword.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoPassword.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfoPassword.backLinkText",
        backLinkRoute: "AccountInfo",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/account-info/language",
    name: "AccountInfoLanguage",
    components: {
      default: () => import("../views/PeerMentorAccountInfoLanguage.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoLanguage.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfoLanguage.backLinkText",
        backLinkRoute: "AccountInfo",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/biography",
    name: "Biography",
    components: {
      default: () => import("../views/PeerMentorBiography.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoLanguage.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfo.backLinkText",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/biography-mentee",
    name: "BiographyMentee",
    components: {
      default: () => import("../views/PeerBiography.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoLanguage.pageTitle",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfo.backLinkText",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentee"],
    },
  },
  {
    path: "/biography/:user",
    name: "ViewBiography",
    components: {
      default: () => import("../views/PeerMentorBiography.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoLanguage.pageTitle",
      },
      Header: {
        backLinkText: "global.buttons.returnHome",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/agreement",
    name: "ViewAgreement",
    components: {
      default: () => import("../views/PeerMentorDocument.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoLanguage.pageTitle",
        document: "agreement",
      },
      Header: {
        backLinkText: "global.buttons.returnHome",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/privacy",
    name: "ViewPrivacy",
    components: {
      default: () => import("../views/PeerMentorDocument.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoLanguage.pageTitle",
        document: "privacy",
      },
      Header: {
        backLinkText: "global.buttons.returnHome",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/terms",
    name: "ViewTerms",
    components: {
      default: () => import("../views/PeerMentorDocument.vue"),
      Header: () => import("../components/PeerMentorHeaderShortNoTitle.vue"),
    },
    props: {
      default: {
        pageTitle: "screens.PeerMentorAccountInfoLanguage.pageTitle",
        document: "terms",
      },
      Header: {
        backLinkText: "global.buttons.returnHome",
        backLinkRoute: "Home",
      },
    },
    meta: {
      allowedUserTypes: ["mentee", "mentor"],
    },
  },
  {
    path: "/admin/join",
    name: "AdminAgreementJoin",
    components: {
      default: () => import("../views/AdminAgreementsJoin.vue"),
      Header: () => import("../components/AdminHeaderAgreements.vue"),
    },
    props: {
      default: {
        pageTitle: "Agreements",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/volunteer",
    name: "AdminAgreementVolunteer",
    components: {
      default: () => import("../views/AdminAgreementsVolunteer.vue"),
      Header: () => import("../components/AdminHeaderAgreements.vue"),
    },
    props: {
      default: {
        pageTitle: "Agreements",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/privacy",
    name: "AdminAgreementPrivacy",
    components: {
      default: () => import("../views/AdminAgreementsPrivacy.vue"),
      Header: () => import("../components/AdminHeaderAgreements.vue"),
    },
    props: {
      default: {
        pageTitle: "Agreements",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/license",
    name: "AdminAgreementLicense",
    components: {
      default: () => import("../views/AdminAgreementsLicense.vue"),
      Header: () => import("../components/AdminHeaderAgreements.vue"),
    },
    props: {
      default: {
        pageTitle: "Agreements",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/",
    name: "AdminHomePeers",
    components: {
      default: () => import("../views/AdminHome.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Administration",
        displayUserType: "peers",
      },
      Header: {
        activeTab: "peers",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/:user",
    name: "AdminHomePeerDisplay",
    components: {
      default: () => import("../views/AdminHome.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Administration",
        displayUserType: "peers",
      },
      Header: {
        activeTab: "peers",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/mentors",
    name: "AdminHomeMentors",
    components: {
      default: () => import("../views/AdminHome.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Administration",
        displayUserType: "mentors",
      },
      Header: {
        activeTab: "mentors",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/admins",
    name: "AdminHomeAdmins",
    components: {
      default: () => import("../views/AdminHome.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Administration",
        displayUserType: "admins",
      },
      Header: {
        activeTab: "admins",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/resources",
    name: "AdminResources",
    components: {
      default: () => import("../views/AdminResources.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Resources",
      },
      Header: {
        activeTab: "",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/profile",
    name: "AdminHomeAdminProfile",
    components: {
      default: () => import("../views/AdminHome.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Administration",
        displayUserType: "admins",
      },
      Header: {
        activeTab: "admins",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/matching",
    name: "AdminMatching",
    components: {
      default: () => import("../views/AdminMatching.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Match Peer",
      },
      Header: {
        activeTab: "matching",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/messages",
    name: "AdminMessaging",
    components: {
      default: () => import("../views/AdminMessaging.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Messages",
      },
      Header: {
        activeTab: "messages",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  {
    path: "/admin/dashboard",
    name: "AdminDashboard",
    components: {
      default: () => import("../views/AdminDashboard.vue"),
      Header: () => import("../components/AdminHeader.vue"),
    },
    props: {
      default: {
        pageTitle: "Dashboard",
      },
      Header: {
        activeTab: "dashboard",
      },
    },
    meta: {
      allowedUserTypes: ["coord"],
    },
  },
  // Catch all route for the 404 handler
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    components: {
      default: () => import("../views/NotFound.vue"),
      Header: () => import("../components/NotificationHeader.vue"),
    },
    props: {
      Header: {
        message: "screens.NotFound.header",
      },
    },
    meta: {
      requiresAuth: false,
    },
  },
  {
    path: "/archivedMentees",
    name: "ArchivedMenteeList",
    components: {
      default: () => import("../views/ArchivedMenteeList.vue"),
      Header: () => import("../components/PeerMentorHeaderShort.vue"),
    },
    props: {
      default: {
        pageTitle: "Archived Mentees",
      },
      Header: {
        backLinkText: "screens.PeerMentorAccountInfo.backLinkText",
        backLinkRoute: { name: "Home" },
      },
    },
    meta: {
      allowedUserTypes: ["mentor"],
    },
  },
];

// Apply default meta values to routes
for (let route of routes) {
  //console.log(route);
  if ("meta" in route === false) {
    route.meta = {
      requiresAuth: true,
      forceActions: true,
      showUrgentAlert: true,
    };
  } else {
    if ("requiresAuth" in route.meta === false) {
      route.meta.requiresAuth = true;
    }
    if ("forceActions" in route.meta === false) {
      route.meta.forceActions = true;
    }
    if ("showUrgentAlert" in route.meta === false) {
      route.meta.showUrgentAlert = route.meta.requiresAuth;
    }
  }
  // we can't force actions if the user isn't authenticated
  if (route.meta.requiresAuth === false) {
    route.meta.forceActions = false;
  }
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // Without this, vue-router leaves the window scroll untouched across navigations, so
  // opening a detail view from a scrolled list lands the user mid-page. savedPosition is
  // only populated for browser back/forward, where restoring the old offset is correct.
  scrollBehavior(to, from, savedPosition) {
    return savedPosition || { top: 0 };
  },
});

// router.beforeEach() handles redirects if user is not authenticated,
// does not have the required permissions, or needs to
// perform an action before we cna serve the route
router.beforeEach((to, from) => {
  //if this is a public route, then anyone can view it
  if (to.meta.requiresAuth === false) {
    console.log("Public view");
    return true;
  }

  // For non-public routes, force authentication
  if (to.meta.requiresAuth && !router.app.user.isAuthenticated()) {
    console.log("Not authenticated");
    console.log(to);
    // For some reason props don't seem to work here, so we'll pass the redirect with the query string
    return { name: "Login", query: { redirect: to.fullPath } };
  }

  // if the user is logged in, check if we need to force an action before serving the route
  let requiredActions = router.app.user.requiresActions();
  console.log(requiredActions);
  console.log(to);
  console.log(requiredActions !== null && to.meta.forceActions);
  if (requiredActions !== null && to.meta.forceActions) {
    return { name: "Consent", query: { redirect: to.fullPath } };
  }

  // Mentees and mentors are shown the splash screen if not gong to interstitial
  if (
    from.name === "Login" &&
    (router.app.userIsMentee() || router.app.userIsMentor()) &&
    to.name !== "PeerMentorSplashScreen" &&
    to.name !== "Consent"
  ) {
    console.log("Router - redirecting to PeerMentorSplashScreen");
    return { name: "PeerMentorSplashScreen", query: { redirect: to.fullPath } };
  }

  // Check if the user is of a type permitted to see the target route; send them home if not
  // if (to.meta.requiresAuth && to.meta.allowedUserTypes.length < 3) {
  //   const userType = router.app.getUserType();
  //   if (!to.meta.allowedUserTypes.includes(userType)) {
  //     console.log("Intercepted - user type perms");
  //     return { name: router.app.getHomeRoute() };
  //   }
  // }

  // If none of the above applies, we can safely serve the requested route
  return true;
});

export default router;
